import { CustomMenuRecord, MenuRecord } from 'apps/website/types/dato';
import { useEffect, useState } from 'react';

declare global {
  interface Window {
    analytics: any;
    Kameleoon: {
      API: {
        Experiments: {
          getActive: () => Array<{
            id: string;
            name: string;
            variations: Array<{
              id: string;
              name: string;
            }>;
          }>;
        };
      };
    };
  }
}

type Category1 = {
  title: string;
  id: string;
  internalUrl?: string | null;
  externalUrl?: string | null;
  urlParams?: string | null;
};

type Category2 = {
  title?: string | null;
  items: {
    title?: string | null;
    internalUrl?: string | null;
    externalUrl?: string | null;
    urlParams?: string | null;
  }[];
};

interface CategoryTracking extends Category1 {
  categories2: Category2[];
}

const prepareCategory = (
  menu: MenuRecord | CustomMenuRecord,
): CategoryTracking[] => {
  const menuItems = menu?.menuItems;
  if (!menuItems) return [];
  const categories: CategoryTracking[] = menuItems.map((item) => {
    const cat1Tmp: CategoryTracking = {
      id: item.id,
      title: item.title ?? '',
      internalUrl:
        item.internalUrl && 'slug' in item.internalUrl
          ? item.internalUrl.slug
          : null,
      categories2: [],
    };
    item.submenuColumns?.forEach((column) => {
      column.submenuSections?.forEach((section) => {
        cat1Tmp.categories2.push({
          title: section.title,
          items:
            section.submenuItems?.map((subItem) => ({
              title: subItem.title,
              internalUrl:
                subItem.internalUrl && 'slug' in subItem.internalUrl
                  ? subItem.internalUrl.slug
                  : null,
              externalUrl: subItem.externalUrl,
              urlParams: subItem.urlParams,
            })) ?? [],
        });
      });
    });

    return cat1Tmp;
  });

  return categories;
};

const preparePayload = (categories: string[]) => {
  const payload = categories.reduce(
    (acc, item, index) => {
      acc[`pageCategory${index + 1}`] = item;
      return acc;
    },
    {} as Record<string, string>,
  );

  return payload;
};

const prepareSpecialCategories = (doc: { __typename: string }) => {
  const pathnameArray = window.location.pathname?.split('/');
  const hompages =
    window.location.host === 'website.dev.payfit.tech' ? [3, 4] : [2, 3];
  if (hompages.includes(pathnameArray.length)) return ['Homepage'];

  if (doc.__typename === 'DatoCmsThankYouPage') {
    return ['ThankYouPage'];
  }

  return false;
};

const getCategories = (
  menu: MenuRecord | CustomMenuRecord,
  doc: { __typename: string },
) => {
  if (!doc || typeof doc !== 'object' || doc === null || !('__typename' in doc))
    return null;

  /**
   * If Special Categories
   */
  const specialCategories = prepareSpecialCategories(doc);
  if (specialCategories) return preparePayload(specialCategories);

  const categories = prepareCategory(menu);
  const subCategories = getSubCategories(doc);
  /** Category 1 */
  let category1: string | undefined | null = categories.find((item) =>
    checkUrl(item),
  )?.title;
  if (category1) return preparePayload([category1, ...subCategories]);

  /** Category 2
   * eg: GenericPage
   */
  let category2: string | undefined | null = null;
  let found = false;

  outerLoop: for (const cat1 of categories) {
    for (const cat2 of cat1.categories2) {
      for (const item of cat2.items) {
        if (checkUrl(item)) {
          category2 = cat2.title;
          category1 = cat1.title;
          found = true;
          break outerLoop; // break all loops
        }
      }
    }
  }

  if (found)
    return preparePayload([
      category1 as string,
      category2 as string,
      ...subCategories,
    ]);

  return {};
};

const getSubCategories = (doc: unknown): string[] => {
  const categories = [];

  if (!doc || typeof doc !== 'object' || doc === null || !('__typename' in doc))
    return [];

  if ('treeParent' in doc) {
    const title = (doc as any).treeParent?.title;
    if (title) categories.push(title);
    return categories;
  }

  if (
    [
      'DatoCmsCategoryPage',
      'DatoCmsSubCategoryPage',
      'DatoCmsBlogArticle',
    ].includes((doc as any).__typename as string)
  ) {
    categories.push('Blog');
    if ('DatoCmsSubCategoryPage' === (doc as any).__typename) {
      categories.push((doc as any).category?.hero?.[0]?.title);
    }
    if ('DatoCmsBlogArticle' === (doc as any).__typename) {
      const category = (doc as any).subCategory?.category?.hero?.[0]?.title;
      categories.push(category);
      categories.push((doc as any).subCategory?.hero?.[0]?.title);
    }
  }

  if ('DatoCmsClientPage' === (doc as any).__typename) {
    if (window.location.pathname.startsWith('/fr/')) {
      categories.push('Clients');
    } else if (window.location.pathname.startsWith('/es/')) {
      categories.push('Clientes');
    } else {
      categories.push('Clients');
    }
  }

  return categories;
};

const checkUrl = (linkObject: {
  internalUrl?: string | null;
  externalUrl?: string | null;
  urlParams?: string | null;
}) => {
  if (typeof window === 'undefined') return [];
  const pathnameArray = window.location.pathname?.split('/');
  let slug = pathnameArray[pathnameArray.length - 2];

  /**
   * Blog pages case
   */
  if (window.location.pathname.startsWith('/fr/fiches-pratiques/'))
    slug = 'fiches-pratiques';
  if (window.location.pathname.startsWith('/es/contenido-practico/'))
    slug = 'contenido-practico';
  if (window.location.pathname.startsWith('/blog/')) slug = 'blog';

  /**
   * Client pages case
   */
  if (window.location.pathname.startsWith('/fr/clients/')) slug = 'clients';
  if (window.location.pathname.startsWith('/es/clientes/')) slug = 'clientes';
  if (window.location.pathname.startsWith('/clients/')) slug = 'all-clients';

  if (linkObject.internalUrl === slug) return true;
  if (linkObject.externalUrl === slug) return true;

  return false;
};

function getKameleoonInfo() {
  if (typeof window === 'undefined') return;
  const experiment = window.Kameleoon?.API?.Experiments?.getActive()?.[0];
  if (!experiment) return;
  const name = experiment?.name;
  const payload = { ab_test_name: name };
  const id = experiment?.id;
  const kameleoonDataJson = localStorage.getItem(`kameleoonExperiment-${id}`);
  if (!kameleoonDataJson) return payload;

  const kameleoonData = JSON.parse(kameleoonDataJson);
  const kameleoonDataValue = kameleoonData.value;
  if (!kameleoonDataValue) return payload;

  const KameleoonVariationId = kameleoonDataValue.split('/')[0];
  if (!KameleoonVariationId) return payload;

  const KameleoonVariationName = experiment.variations.find(
    (variation) => variation.id.toString() === KameleoonVariationId,
  )?.name;

  return {
    abTestName: name,
    abTestVariation: KameleoonVariationName,
  };
}

export const getPageViewPayload = (
  menu: MenuRecord | CustomMenuRecord,
  doc: any,
) => {
  const categories = getCategories(menu, doc);
  const kameleoonInfo = getKameleoonInfo();

  let envLanguage = 'en';
  if (window.location.pathname.includes('/fr/')) envLanguage = 'fr';
  if (window.location.pathname.includes('/es/')) envLanguage = 'es';

  const origin = window.location.origin;
  const pathname = window.location.pathname;
  const referrer = origin + pathname;
  return {
    ...kameleoonInfo,
    ...categories,
    envLanguage,
    envWork: process.env.GATSBY_ENV === 'production' ? 'Prod' : 'Dev',
    title: doc?.metaFields?.title,
    referrer,
  };
};

export const useTrackingReady = () => {
  const [trackingReady, setTrackingReady] = useState(() =>
    typeof window !== 'undefined'
      ? sessionStorage.getItem('trackingReady') || 'false'
      : 'false',
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const originalSetItem = Storage.prototype.setItem;

    Storage.prototype.setItem = function (key: string, value: string) {
      if (key === 'trackingReady') {
        setTrackingReady(value);
      }

      originalSetItem.call(this, key, value);
    };

    if (trackingReady === 'true') {
      Storage.prototype.setItem = originalSetItem;
    }

    return () => {
      Storage.prototype.setItem = originalSetItem;
    };
  }, [trackingReady]);

  return trackingReady;
};
